import { Dashboard } from 'context/Dashboards';
import { Plugin } from 'context/Plugin';
import {
  customSignupProducts,
  dashboardSignupProduct,
  datasourceSignupProduct,
  pluginSignupProduct,
} from './CustomSignup.data';
import { CustomSignupProductType } from './types';

interface Context {
  dashboard?: Dashboard | null;
  plugin?: Plugin | null;
}

export const signUpVariant = (search: string, context?: Context): CustomSignupProductType | undefined => {
  // If we have a known dashboard in context, prioritize sending the user straight to using that.
  if (context?.dashboard) {
    return dashboardSignupProduct({
      key: `${context.dashboard.id}-${context.dashboard.slug}`,
      redirect: `dashboard/template?gnetId=${context.dashboard.id}&mappings=[]`,
    });
  }

  // If we have a known datasource in context, prioritize sending the user straight to using that.
  if (context?.plugin) {
    if (context.plugin.typeCode === 'datasource') {
      return datasourceSignupProduct({
        key: context.plugin.slug,
        name: context.plugin.name,
      });
    }
  }

  // Strip `to` before matching: it contains encoded URLs that can false-match product keys.
  // And, sort the remaining params for consistent matching.
  const params = new URLSearchParams(search);
  params.delete('to');
  params.sort();
  const cleanSearch = params.toString();

  // If dashboards is in the search params, but no specific dashboard is specified, show the generic dashboards signup.
  if (params.get('pg') === 'dashboards') {
    return dashboardSignupProduct({
      key: 'dashboards',
      redirect: '',
    });
  }

  // Try to find a matching custom signup product based on the search params.
  const custom = Object.values(customSignupProducts).find((s) => {
    if (s.exactKeyMatch) {
      return params.has(s.key) || params.get('pg') === s.key;
    }

    // If the key can be parsed as search params, sort them for consistent matching.
    if (s.key.includes('=')) {
      try {
        const sParams = new URLSearchParams(s.key);
        sParams.sort();
        const cleanKey = sParams.toString();
        return cleanSearch.includes(cleanKey);
      } catch {}
    }

    return cleanSearch.includes(s.key);
  });
  if (custom) {
    return custom;
  }

  // If we have a known plugin in context, but it's not a datasource, send the user to the install page.
  // Do this after trying to match custom products, to allow products that are plugins to have custom flows.
  if (context?.plugin) {
    return pluginSignupProduct({
      key: context.plugin.slug,
      name: context.plugin.name,
    });
  }

  return undefined;
};
